import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useLanguage } from './language-context';
import { useLayer } from './layer-stack';
import { useResource } from './use-resource';
import { NavIcon } from './nav-icon';
import { canAccess, navItemFor } from './nav-items';
import { notificationLook, notificationSection } from './notification-target';
import { announceInboxChanged, useInboxChanged } from './inbox-changed';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import { formatTimeAgo } from '@bookingdei/i18n/format-date';
import { localiseDigits } from '@bookingdei/i18n/format-number';
import type { AdminNotification, AdminNotificationList } from '../mock/seeds/schema-types';

// CB-17 notification tray behind the header bell (design D:303-340, script
// D:8544-8594) on the admin inbox operations (API-198): list, mark read (own
// state, exempt from audit — API-26a), dismiss. All / Unread tabs, "Mark all
// read", per-row dismiss, "See all →" (Notifications section, inbox) and
// "Notification settings" (alerts tab). A row opens the section its entity lives
// in (notification-target.ts) — or the inbox when the viewer's role cannot open it —
// and names the category's action (design c.action). Writes announce on
// inbox-changed.ts so the Notifications section and the bell stay in step.

export const TRAY_ROWS = 8;

export interface AdminInbox {
  items: AdminNotification[];
  unread: number;
  error: unknown;
  loading: boolean;
  reload: () => void;
}

/** The caller's inbox (non-dismissed rows) + unread count, for the bell and the tray. */
export function useAdminInbox(): AdminInbox {
  const { data, error, loading, reload } = useResource<AdminNotificationList>(
    (signal) =>
      api.GET('/admin/notifications', { signal, params: { query: { state: 'all' } } }) as Promise<AdminNotificationList>,
    [],
  );
  useInboxChanged(reload);
  const items = data?.items ?? [];
  const unread = data?.unread_count ?? items.filter((n) => !n.read_at).length;
  return { items, unread, error, loading, reload };
}

export function NotificationTray({
  open,
  onClose,
  inbox,
  grants,
}: {
  open: boolean;
  onClose: () => void;
  inbox: AdminInbox;
  grants: readonly string[];
}) {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'all' | 'unread'>('all');
  const panelRef = useRef<HTMLDivElement>(null);
  useLayer(open, onClose, { blocking: false });

  useEffect(() => {
    if (!open) return;
    setTab('all');
    panelRef.current?.focus();
  }, [open]);

  const { items, unread } = inbox;
  const num = (n: number) => localiseDigits(lang, n);

  const markRead = useCallback(
    async (ids?: string[]) => {
      try {
        await api.POST('/admin/notifications/read', { body: ids ? { ids } : {} });
      } finally {
        announceInboxChanged();
      }
    },
    [],
  );

  const dismiss = async (id: string) => {
    try {
      await api.POST('/admin/notifications/{id}/dismiss', { params: { path: { id } } });
    } finally {
      announceInboxChanged();
    }
  };

  const openRow = (n: AdminNotification) => {
    const section = notificationSection(n);
    const target = canAccess(grants, section) ? section : 'notifications';
    if (!n.read_at) void markRead([n.id]);
    onClose();
    navigate(navItemFor(target).to);
  };

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  if (!open || typeof document === 'undefined') return null;
  const rows = items.filter((n) => tab === 'all' || !n.read_at).slice(0, TRAY_ROWS);
  const now = new Date();
  const title = (n: AdminNotification) => (lang === 'bn' && n.title_bn ? n.title_bn : n.title_en);

  return createPortal(
    <div className="tray-layer">
      <div className="tray__backdrop" onClick={onClose} aria-hidden="true" data-backdrop />
      <div ref={panelRef} tabIndex={-1} className="tray" role="dialog" aria-label={t('admin.shell.notifications')}>
        <div className="tray__head">
          <span className="tray__title">{t('admin.shell.notifications')}</span>
          {unread > 0 && <span className="tray__count">{num(unread)}</span>}
          <span className="tray__spacer" />
          {unread > 0 && (
            <button type="button" className="tray__mark" onClick={() => void markRead()}>
              {t('admin.shell.tray.mark_all')}
            </button>
          )}
        </div>
        <div className="tray__tabs" role="group">
          <button type="button" className={tab === 'all' ? 'tray__tab is-active' : 'tray__tab'} aria-pressed={tab === 'all'} onClick={() => setTab('all')}>
            {formatMessage(lang, 'admin.shell.tray.all', { n: num(items.length) })}
          </button>
          <button type="button" className={tab === 'unread' ? 'tray__tab is-active' : 'tray__tab'} aria-pressed={tab === 'unread'} onClick={() => setTab('unread')}>
            {formatMessage(lang, 'admin.shell.tray.unread', { n: num(unread) })}
          </button>
        </div>
        <div className="tray__body">
          {inbox.error ? (
            <div className="tray__empty" role="alert">
              {t('common.error_title')}
            </div>
          ) : rows.length === 0 ? (
            <div className="tray__empty">{t(tab === 'unread' ? 'admin.shell.tray.empty_unread' : 'admin.shell.tray.empty_all')}</div>
          ) : (
            <ul className="tray__list">
              {rows.map((n) => {
                const look = notificationLook(n.category);
                const action = t(look.action);
                return (
                  <li key={n.id} className={n.read_at ? 'tray__row' : 'tray__row is-unread'}>
                    <button type="button" className="tray__open" onClick={() => openRow(n)}>
                      <span className={`tray__icon tray__icon--${look.tone}`}>
                        <NavIcon name={look.icon} size={17} />
                      </span>
                      <span className="tray__text">
                        <span className="tray__row-title">{title(n)}</span>
                        <span className="tray__meta">
                          <span>{formatTimeAgo(lang, new Date(n.created_at), now)}</span>
                          <span className="tray__dot-sep" aria-hidden="true" />
                          <span className="tray__action">{action}</span>
                        </span>
                      </span>
                      {!n.read_at && <span className="tray__unread" aria-hidden="true" />}
                    </button>
                    <button
                      type="button"
                      className="tray__dismiss"
                      aria-label={`${t('admin.shell.tray.dismiss')}: ${title(n)}`}
                      onClick={() => void dismiss(n.id)}
                    >
                      ✕
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="tray__foot">
          <button type="button" className="tray__see-all" onClick={() => go(navItemFor('notifications').to)}>
            {t('admin.shell.tray.see_all')}
          </button>
          <button type="button" className="tray__settings" onClick={() => go(`${navItemFor('notifications').to}?tab=alerts`)}>
            {t('admin.shell.tray.settings')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
